import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  Container,
  Stack,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  CheckCircle as CheckCircleIcon,
  Book as BookIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import ReactConfetti from "react-confetti";
import { keyframes } from "@mui/material/styles";
import { CartContext } from "../../contexts/CartContext";

// Advanced Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "70vh",
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[100]} 100%)`,
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4, 0),
}));

const SuccessContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  animation: `${slideIn} 0.6s ease-out`,
}));

const MainCard = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  padding: theme.spacing(4),
  color: theme.palette.common.white,
  position: "relative",
  overflow: "hidden",
  textAlign: "center",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "30%",
    background: "linear-gradient(transparent, rgba(0,0,0,0.1))",
  },
}));

const SuccessIconContainer = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  animation: `${pulseAnimation} 2s infinite ease-in-out`,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: "center",
}));

const ActionButton = styled(Link)(({ theme, variant }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius * 2,
  fontSize: "1rem",
  fontWeight: 600,
  textDecoration: "none",
  transition: "all 0.3s ease",
  ...(variant === "primary"
    ? {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        transform: "translateY(-2px)",
      },
    }
    : {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      "&:hover": {
        backgroundColor: theme.palette.grey[100],
        transform: "translateY(-2px)",
      },
    }),
}));

const UnifiedSuccess = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [confettiRunning, setConfettiRunning] = useState(true);
  const { clearCart } = useContext(CartContext);

  useEffect(() => {
    // Clear the cart
    clearCart();
  }, [clearCart]);

  return (
    <PageContainer>
      {confettiRunning && (
        <ReactConfetti
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
          }}
          numberOfPieces={isMobile ? 100 : 200}
          recycle={false}
          colors={[
            theme.palette.primary.light,
            theme.palette.primary.main,
            theme.palette.success.light,
            theme.palette.success.main,
            "#FFD700",
          ]}
        />
      )}

      <SuccessContainer maxWidth="sm">
        <MainCard>
          <HeaderSection>
            <SuccessIconContainer>
              <CheckCircleIcon sx={{ fontSize: 40 }} />
            </SuccessIconContainer>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Order Successful!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Thank you for your purchase
            </Typography>
          </HeaderSection>

          <ContentSection>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your order has been successfully placed. You will receive a confirmation email shortly.
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <ActionButton to="/profile" variant="primary">
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>View Order</span>
                  <ArrowForwardIcon />
                </Stack>
              </ActionButton>
              <ActionButton to="/books">
                <Stack direction="row" spacing={1} alignItems="center">
                  <BookIcon />
                  <span>Continue Shopping</span>
                </Stack>
              </ActionButton>
            </Box>
          </ContentSection>
        </MainCard>
      </SuccessContainer>
    </PageContainer>
  );
};

export default UnifiedSuccess;
